import { useRef, useState } from "react";
import { Pencil, Plus, RefreshCw, Trash2, Eye } from "lucide-react";
import { Alert, Button, DataTable, Modal, Tag, useToast } from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  capturePrivateSession,
  isPrivateSessionCurrent,
} from "@/lib/api/private-session";
import {
  useWhatsappQuery,
  useWhatsappWrite,
  whatsappFailureKey,
  isUncertainWrite,
} from "../services/useWhatsapp";
import { templateInUse } from "../services/template-components";
import {
  languageDisplayName,
  providerLabel,
  templateDisplayName,
} from "../services/value-format";
import type {
  WhatsappAccount,
  WhatsappTemplate,
} from "../types/whatsapp.types";
import { NoRecords, QueryState, StatusBadge, WriteFailure } from "./Shared";
import { mono } from "./styles";
import { TemplateEditor } from "./TemplateEditor";
const copy = keys.whatsapp;
export function TemplatesPanel({
  account,
  query,
  after,
  onNext,
  onPrevious,
}: {
  account: WhatsappAccount;
  query: string;
  after: string | null;
  onNext: (after: string) => void;
  onPrevious: () => void;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const result = useWhatsappQuery(
    "templates",
    query,
    account.readiness.managementReady,
  );
  const write = useWhatsappWrite();
  const [lifetime] = useState(capturePrivateSession);
  const busy = useRef(false);
  const [editing, setEditing] = useState<WhatsappTemplate | "new">();
  const [deleting, setDeleting] = useState<WhatsappTemplate>();
  /** The last delete that failed: its reason, and the template to try again. */
  const [failure, setFailure] = useState<{ message: string; template: WhatsappTemplate }>();
  const [uncertain, setUncertain] = useState(false);
  const toast = useToast();
  async function remove(target = deleting) {
    if (!target || busy.current || uncertain) return;
    busy.current = true;
    try {
      await write.mutateAsync({
        method: "DELETE",
        path: `templates/${encodeURIComponent(target.id)}`,
      });
      if (isPrivateSessionCurrent(lifetime)) {
        setFailure(undefined);
        setDeleting(undefined);
        toast.show({ message: `${target.name} (${target.language}): ${t(copy.deleted)}` });
      }
    } catch (e) {
      if (isPrivateSessionCurrent(lifetime)) {
        const unknown = isUncertainWrite(e);
        setUncertain(Boolean(unknown));
        setFailure({ message: t(whatsappFailureKey(e, true)), template: target });
        setDeleting(undefined);
      }
    } finally {
      busy.current = false;
    }
  }
  /** Re-reads the list; the result changes no data, so a neutral toast. */
  async function refresh() {
    const next = await result.refetch();
    if (!next.isError && isPrivateSessionCurrent(lifetime))
      toast.show({ tone: "neutral", icon: RefreshCw, message: t("whatsapp.toastTemplatesRefreshed") });
  }
  if (!account.readiness.managementReady)
    return <Alert tone="warn" title={t("whatsapp.setupIncompleteTitle")}>{t(copy.configRequired)}</Alert>;
  const createButton = (
    <Button
      variant="primary"
      size="sm"
      icon={Plus}
      onClick={() => setEditing("new")}
    >
      {t(copy.newTemplate)}
    </Button>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <div className="rd-spacer" />
        <Button
          variant="secondary"
          size="sm"
          icon={RefreshCw}
          loading={result.isRefetching}
          loadingLabel={t("whatsapp.refreshing")}
          onClick={() => void refresh()}
        >
          {t(copy.refresh)}
        </Button>
        {createButton}
      </div>
      {failure && (
        <WriteFailure
          title={t("whatsapp.templateDeleteFailed", { name: failure.template.name, language: failure.template.language })}
          message={failure.message}
          uncertain={uncertain}
          retrying={write.isPending}
          onTryAgain={() => void remove(failure.template)}
          onCheckedOutcome={() => {
            setUncertain(false);
            setFailure(undefined);
          }}
        />
      )}
      <QueryState loading={result.isLoading} error={result.error} retry={result.refetch} page={t(copy.templates).toLocaleLowerCase(locale)} rows={6}>
        {result.data && (
          <DataTable
            label={t(copy.templates)}
            rows={result.data.items}
            rowKey={(r) => r.id}
            minWidth={800}
            empty={<NoRecords title={t("whatsapp.templatesEmptyTitle")} text={t("whatsapp.templatesEmptyText")} action={createButton} />}
            footer={
              <nav className="rd-table-foot" aria-label={t(copy.pagination)}>
                <Button variant="ghost" size="sm" disabled={!after} onClick={onPrevious}>
                  {t(copy.previous)}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!result.data.after}
                  onClick={() => {
                    if (result.data?.after) onNext(result.data.after);
                  }}
                >
                  {t(copy.next)}
                </Button>
              </nav>
            }
            columns={[
              {
                key: "name",
                label: t(copy.name),
                width: 2,
                render: (r) => (
                  <div className="rd-cell-stack" style={{ gap: 2 }}>
                    <span className="rd-item-name" style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                      <bdi>{templateDisplayName(r.name, t)}</bdi>
                    </span>
                    {templateDisplayName(r.name, t) !== r.name && (
                      <span className="rd-cell-sub" style={mono}>
                        <bdi>{r.name}</bdi>
                      </span>
                    )}
                    <span className="rd-cell-sub">
                      <bdi>{languageDisplayName(r.language, locale)}</bdi>
                    </span>
                    {templateInUse(r, account.bindings) && (
                      <span>
                        <Tag tone="blue">{t(copy.inUse)}</Tag>
                      </span>
                    )}
                  </div>
                ),
              },
              {
                key: "status",
                label: t(copy.status),
                render: (r) => (
                  <div className="rd-cell-stack">
                    <StatusBadge value={r.status} />
                    {r.rejected_reason?.trim() && r.rejected_reason.trim().toUpperCase() !== "NONE" && (
                      <span className="rd-cell-sub" style={{ whiteSpace: "normal" }}>
                        {t(copy.rejectionReason)}: {r.rejected_reason}
                      </span>
                    )}
                  </div>
                ),
              },
              {
                key: "category",
                label: t(copy.category),
                render: (r) => (
                  <span style={{ color: "var(--rd-text-secondary)" }}>{providerLabel(r.category, t)}</span>
                ),
              },
              {
                key: "actions",
                label: t(copy.actions),
                width: "minmax(220px, auto)",
                align: "end",
                render: (r) => {
                  const inUse = templateInUse(r, account.bindings);
                  return (
                    <span style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 2 }}>
                      <Button variant="ghost" size="sm" icon={inUse ? Eye : Pencil} onClick={() => setEditing(r)}>
                        {t(inUse ? copy.inspectTemplate : copy.editTemplate)}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Trash2}
                        disabled={inUse || write.isPending || uncertain}
                        onClick={() => setDeleting(r)}
                      >
                        {t(copy.deleteTemplate)}
                      </Button>
                    </span>
                  );
                },
              },
            ]}
          />
        )}
      </QueryState>
      {editing && (
        <TemplateEditor
          key={editing === "new" ? "new" : editing.id}
          template={editing === "new" ? undefined : editing}
          inUse={editing !== "new" && templateInUse(editing, account.bindings)}
          onClose={() => setEditing(undefined)}
          onSaved={(name) => {
            setEditing(undefined);
            toast.show({
              message: `${name}: ${t(copy.writeAccepted)}`,
              actionLabel: t(copy.refresh),
              onAction: () => void result.refetch(),
            });
          }}
        />
      )}
      <Modal
        open={Boolean(deleting)}
        onClose={() => {
          if (!write.isPending) setDeleting(undefined);
        }}
        title={t(copy.deleteTemplate)}
        actions={
          <>
            <Button variant="ghost" muted disabled={write.isPending} onClick={() => setDeleting(undefined)}>
              {t(copy.cancel)}
            </Button>
            <Button
              variant="danger"
              loading={write.isPending}
              loadingLabel={t("whatsapp.deleting")}
              onClick={() => void remove()}
            >
              {t(copy.deleteTemplate)}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 14 }}>
          {t(copy.deleteConfirm, {
            name: deleting?.name ?? "",
            language: deleting?.language ?? "",
          })}
        </p>
      </Modal>
    </div>
  );
}
